import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Copy,
  ExternalLink,
  Lightbulb,
  Rocket,
  Send,
  Sparkles,
  Timer,
} from "lucide-react";
import { api } from "../lib/api";

async function fetchPlatformIdeas() {
  const { data } = await api.get("/platform-ideas");
  return data;
}

function useCountdown(targetIso) {
  const [remaining, setRemaining] = useState(() => diff(targetIso));
  useEffect(() => {
    if (!targetIso) return undefined;
    const t = setInterval(() => setRemaining(diff(targetIso)), 1000);
    return () => clearInterval(t);
  }, [targetIso]);
  return remaining;
}

function diff(iso) {
  if (!iso) return { h: 0, m: 0, s: 0, expired: true };
  const end = new Date(iso).getTime();
  const now = Date.now();
  const ms = Math.max(0, end - now);
  if (ms <= 0) return { h: 0, m: 0, s: 0, expired: true };
  const total = Math.floor(ms / 1000);
  return {
    h: Math.floor(total / 3600),
    m: Math.floor((total % 3600) / 60),
    s: total % 60,
    expired: false,
  };
}

function pad(n) {
  return String(n).padStart(2, "0");
}

function useIdeaToBot() {
  const navigate = useNavigate();
  return (idea) => {
    try {
      const refs = [
        {
          kind: "paste",
          label: `PLATFORM_IDEA · ${idea.title.slice(0, 40)}`,
          text: `${idea.title}\n\n${idea.one_liner}\n\nTarget user: ${idea.target_user}\nWhy now: ${idea.why_now}\nHook metric: ${idea.hook_metric}\n\n${idea.prompt_seed}`,
        },
      ];
      sessionStorage.setItem("mindstein_pending_refs", JSON.stringify(refs));
      sessionStorage.setItem(
        "mindstein_pending_prompt",
        idea.prompt_seed || "",
      );
      toast.success("IDEA_ATTACHED — sending you to the bot");
      navigate("/bot");
    } catch (e) {
      toast.error("HANDOFF_FAILED", { description: String(e) });
    }
  };
}

function IdeaCard({ idea, onSendToBot }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    const text = `${idea.title}\n${idea.one_liner}\n\n${idea.prompt_seed}`;
    navigator.clipboard
      ?.writeText(text)
      .then(() => {
        setCopied(true);
        toast.success("COPIED", { description: "Paste into anywhere." });
        setTimeout(() => setCopied(false), 1600);
      })
      .catch(() => toast.error("COPY_FAILED"));
  };
  return (
    <div
      data-testid={`pig-idea-card-${idea.id}`}
      className="border border-[#27272A] bg-[#0A0A0A] p-3 flex flex-col hover:border-amber-400/40 transition-colors"
    >
      <div className="flex items-center gap-2 text-[9.5px] font-mono uppercase tracking-[0.18em] text-zinc-500">
        <span className="text-amber-300">{idea.category}</span>
        <span>·</span>
        <span className="truncate">{idea.target_user}</span>
      </div>
      <h4 className="mt-1 text-[14px] leading-snug text-white">
        {idea.title}
      </h4>
      <p className="mt-1 text-[12.5px] text-zinc-300 leading-relaxed">
        {idea.one_liner}
      </p>
      <dl className="mt-2 space-y-1 text-[11.5px] font-mono">
        <div className="flex gap-2">
          <dt className="text-emerald-300 shrink-0">WHY_NOW</dt>
          <dd className="text-zinc-400">{idea.why_now}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="text-purple-300 shrink-0">PMF_METRIC</dt>
          <dd className="text-zinc-400">{idea.hook_metric}</dd>
        </div>
      </dl>
      <div className="mt-3 flex flex-wrap items-center gap-2 pt-3 border-t border-[#161616]">
        <button
          data-testid={`pig-copy-${idea.id}`}
          onClick={copy}
          className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:bg-[#141414] hover:text-white"
        >
          <Copy size={11} /> {copied ? "COPIED" : "COPY"}
        </button>
        <button
          data-testid={`pig-use-in-bot-${idea.id}`}
          onClick={() => onSendToBot(idea)}
          className="inline-flex items-center gap-1 h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-amber-400 bg-amber-400/10 text-amber-200 hover:bg-amber-400 hover:text-black transition-colors"
          title="Run this idea through MindStein — roadmap, research, technical validation"
        >
          <Send size={11} /> RUN_IN_MINDSTEIN
        </button>
      </div>
    </div>
  );
}

export default function PlatformIdeaGenerator() {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["platform-ideas"],
    queryFn: fetchPlatformIdeas,
    // Poll every 5s while the background generator is warming up; fall back
    // to a 5-minute soft refresh once the batch is fresh.
    refetchInterval: (query) => {
      const d = query.state.data;
      if (!d) return 5000;
      if (d.status && d.status !== "fresh") return 5000;
      return 5 * 60 * 1000;
    },
    retry: 1,
  });

  const sendToBot = useIdeaToBot();
  const countdown = useCountdown(data?.next_refresh_at);

  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const ideas = data?.ideas || [];
    if (!q.trim()) return ideas;
    const needle = q.trim().toLowerCase();
    return ideas.filter((i) =>
      [i.title, i.one_liner, i.category, i.target_user, i.why_now, i.hook_metric]
        .filter(Boolean)
        .join(" ")
        .toLowerCase()
        .includes(needle),
    );
  }, [data, q]);

  return (
    <section
      data-testid="platform-idea-generator"
      className="mt-6 border border-amber-400/30 bg-[#0A0A0A]"
    >
      <header className="px-4 py-3 border-b border-amber-400/20 flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-2">
          <Rocket size={14} className="text-amber-300" />
          <h2 className="font-mono text-[15px] tracking-tight text-white">
            PLATFORM_IDEA_GENERATOR
          </h2>
          <span className="text-[9.5px] font-mono uppercase tracking-[0.2em] text-amber-300 border border-amber-400/40 bg-amber-400/10 px-1.5 py-0.5">
            24H_ROLLING · 20_IDEAS
          </span>
        </div>
        <div
          data-testid="pig-countdown"
          className="ml-auto inline-flex items-center gap-2 text-[11px] font-mono tabnum text-amber-200 border border-amber-400/30 bg-[#0E0E0E] px-2 py-1"
          title="Next refresh timer"
        >
          <Timer size={11} />
          NEXT_REFRESH
          <span className="text-white">
            {pad(countdown.h)}:{pad(countdown.m)}:{pad(countdown.s)}
          </span>
        </div>
      </header>

      <div className="px-4 py-3 space-y-3 border-b border-[#161616]">
        <p className="text-[12.5px] text-zinc-300 leading-relaxed max-w-3xl">
          <Lightbulb size={12} className="inline text-amber-300 mr-1" />
          20 fresh <span className="text-white">platform &amp; product ideas</span>{" "}
          curated every 24 hours for founders, PMs and R&amp;D engineers. Copy
          any card in one click, or run it directly through MindStein to
          generate a research roadmap, competitive landscape, technical
          validation, and TRL assessment — grounded in verified sources.
        </p>
        <div>
          <div className="text-[9.5px] font-mono uppercase tracking-[0.2em] text-zinc-500">
            IDEA_DISCOVERY_LINKS
          </div>
          <div
            data-testid="pig-curated-links"
            className="mt-2 flex flex-wrap gap-2"
          >
            {(data?.curated_links || []).map((l) => (
              <a
                key={l.name}
                data-testid={`pig-link-${l.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                href={l.url}
                target="_blank"
                rel="noopener noreferrer"
                title={l.blurb}
                className="inline-flex items-center gap-1 h-7 px-2 text-[10.5px] font-mono uppercase tracking-wider border border-[#27272A] bg-[#0E0E0E] text-zinc-300 hover:border-emerald-400/50 hover:text-emerald-200"
              >
                <ExternalLink size={10} /> {l.name}
              </a>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input
            data-testid="pig-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="FILTER · category · target user · keyword"
            className="h-7 flex-1 min-w-[240px] bg-[#0E0E0E] border border-[#27272A] px-2 text-[11px] font-mono text-white placeholder:text-zinc-600 outline-none focus:border-amber-400/50"
          />
          <span className="text-[10px] font-mono text-zinc-500 tabnum">
            {filtered.length} / {data?.ideas?.length || 0}
          </span>
          <button
            data-testid="pig-refresh"
            onClick={() => refetch()}
            className="h-7 px-2 text-[10px] font-mono uppercase tracking-wider border border-[#27272A] text-zinc-300 hover:bg-[#141414] hover:text-white inline-flex items-center gap-1"
            title="Fetch the latest batch (still rolls every 24h)"
          >
            <Sparkles size={10} /> REFETCH
          </button>
        </div>
      </div>

      <div className="p-3">
        {isLoading && (
          <div className="p-8 text-center text-[11px] font-mono uppercase tracking-wider text-amber-300 animate-pulse">
            GENERATING_20_IDEAS · this can take ~30s on the first hit of the day
          </div>
        )}
        {!isLoading && data?.status === "seeding" && (data?.ideas?.length || 0) === 0 && (
          <div
            data-testid="pig-seeding-banner"
            className="p-6 text-center text-[11px] font-mono text-amber-200 border border-amber-400/30 bg-amber-500/[0.05] animate-pulse"
          >
            WARMING_UP · MindStein is authoring your first 20 platform ideas —
            usually ready in 30-60 seconds. This page auto-refreshes.
          </div>
        )}
        {!isLoading && data?.status === "regenerating" && (data?.ideas?.length || 0) > 0 && (
          <div
            data-testid="pig-regenerating-banner"
            className="mb-3 px-3 py-2 text-[10.5px] font-mono uppercase tracking-wider text-amber-200 border border-amber-400/30 bg-amber-500/[0.04]"
          >
            REFRESHING_IN_BACKGROUND · showing the last batch until the new one lands.
          </div>
        )}
        {error && !isLoading && (
          <div className="p-6 text-center text-[11px] font-mono text-rose-300 border border-rose-400/30 bg-rose-500/[0.05]">
            IDEA_GENERATOR_UNAVAILABLE — try REFETCH in a moment.
          </div>
        )}
        {!isLoading && !error && data?.status !== "seeding" && filtered.length === 0 && (
          <div className="p-8 text-center text-[11px] font-mono text-zinc-500">
            NO_IDEAS_MATCH_FILTER — clear the search box.
          </div>
        )}
        {filtered.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {filtered.map((idea) => (
              <IdeaCard key={idea.id} idea={idea} onSendToBot={sendToBot} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
